import { Component, Inject, OnInit, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA,MatDialog, MatDialogRef } from '@angular/material/dialog';
import { CustomMessageService } from '@app/shared/components/custom-message/custom-message.service';
import { LoggerService } from '@app/logger/logger.service';
import { StockEquipmentsService } from '@app/shared/services/entities/stockEquipments.service';
import {DocumentsUploadDownloadFormComponent } from '@app/inventoryEquipments/documents-upload-download-form/documents-upload-download-form.component';
import { duration } from 'moment';

import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MaterialModule } from '@app/material.module';
const log = new LoggerService('ENTRY EXIT DATE FORM COMPONENT');

interface EntryExitDate {
  contract: string;
  date: string;
  fileNumber: string;
  pdf: boolean;
}

@Component({
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MaterialModule,
  ],
  selector: 'app-inventoryEquipmentsEntryExitDate-form',
  templateUrl: './inventoryEquipmentsEntryExitDate-form.component.html',
  styleUrls: ['./inventoryEquipmentsEntryExitDate-form.component.scss']
})
export class InventoryEquipmentsEntryExitDateFormComponent implements OnInit {

  public formGroup: FormGroup;
  public activeContractList = null;
  public contractList = null;
  public contractObj = null;
  public record = null;
  public entryDate: EntryExitDate[] = [];
  public exitDate: EntryExitDate[] = [];
  public minDate = new Date(); // Fecha mínima para el DatePicker

  constructor(
    public dialog: MatDialog,
    public dialogRef: MatDialogRef<InventoryEquipmentsEntryExitDateFormComponent>,
    private customMessageService: CustomMessageService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private formBuilder: FormBuilder,
    private inventoryEquipmentsService: StockEquipmentsService,
    private cdr: ChangeDetectorRef
  ) { }

  async ngOnInit() {
    
    this.activeContractList = this.data.contract.filter(element => element.status === true) || [];
    this.contractList = this.data.contract;
    this.contractObj = this.data.contractObj;
    this.record = this.data.record;
    this.entryDate = this.data.record.entryDate || [];
    this.exitDate = this.data.record.exitDate || [];

    this.formGroup = this.formBuilder.group({
      // Formularios para agregar nuevas fechas de entrada
      newEntryContract: [null, Validators.required],
      newEntryDate: [null, Validators.required],
      newEntryFileNumber: ['', Validators.required],
      newEntryPDF: [false, Validators.required],
      
      // Formularios para agregar nuevas fechas de salida
      newExitContract: [null, Validators.required],
      newExitDate: [null, Validators.required],
      newExitFileNumber: ['', Validators.required],
      newExitPDF: [false, Validators.required],
    });

    log.debug('Datos iniciales:', {
      entryDate: this.entryDate,
      exitDate: this.exitDate,
      activeContractList: this.activeContractList
    });
  }

  /**@description Comparar contratos para mat-select */
  compareContracts(c1: any, c2: any): boolean {
    return c1 && c2 ? c1.id === c2.id : c1 === c2;
  }

  /**@description Obtener fechas de entrada con información del contrato */
  getEntryDatesWithContracts() {
    return this.entryDate
      .map(entry => ({
        ...entry,
        contractName: this.contractObj[entry.contract] || 'Contrato no encontrado'
      }))
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()); // Más recientes primero
  }

  /**@description Obtener fechas de salida con información del contrato */
  getExitDatesWithContracts() {
    return this.exitDate
      .map(exit => ({
        ...exit,
        contractName: this.contractObj[exit.contract] || 'Contrato no encontrado'
      }))
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()); // Más recientes primero
  }

  /**@description Agregar nueva fecha de entrada */
  addEntryDate() {
    const contract = this.formGroup.get('newEntryContract')?.value;
    const date = this.formGroup.get('newEntryDate')?.value;
    const fileNumber = this.formGroup.get('newEntryFileNumber')?.value?.trim();
    const pdf = this.formGroup.get('newEntryPDF')?.value;

    // Validaciones
    if (!contract) {
      this.customMessageService.open({
        title: 'Validación',
        type: 'warning',
        message: 'Seleccione un contrato para la fecha de entrada.',
        dismissBtn: true
      });
      return;
    }

    if (!date) {
      this.customMessageService.open({
        title: 'Validación',
        type: 'warning',
        message: 'Seleccione una fecha de entrada.',
        dismissBtn: true
      });
      return;
    }

    if (!fileNumber) {
      this.customMessageService.open({
        title: 'Validación',
        type: 'warning',
        message: 'Ingrese un número de folio para la fecha de entrada.',
        dismissBtn: true
      });
      return;
    }

    // Convertir fecha a string ISO
    const dateString = new Date(date).toISOString().split('T')[0];

    // Verificar duplicados
    const existingEntry = this.entryDate.find(
      entry => entry.contract === contract.id && entry.date === dateString
    );

    if (existingEntry) {
      this.customMessageService.open({
        title: 'Validación',
        type: 'warning',
        message: 'Ya existe una fecha de entrada para este contrato en esta fecha.',
        dismissBtn: true
      });
      return;
    }

    // Agregar nueva entrada
    this.entryDate.push({
      contract: contract.id,
      date: dateString,
      fileNumber: fileNumber,
      pdf:pdf
    });

    // Limpiar formulario
    this.formGroup.patchValue({
      newEntryContract: null,
      newEntryDate: null,
      newEntryFileNumber: '',
      newEntryPDF: false
    });

    this.cdr.detectChanges();
    log.debug('Nueva fecha de entrada agregada:', this.entryDate);
  }

  /**@description Agregar nueva fecha de salida */
  addExitDate() {
    const contract = this.formGroup.get('newExitContract')?.value;
    const date = this.formGroup.get('newExitDate')?.value;
    const fileNumber = this.formGroup.get('newExitFileNumber')?.value?.trim();
    const pdf = this.formGroup.get('newExitPDF')?.value;

    // Validaciones
    if (!contract) {
      this.customMessageService.open({
        title: 'Validación',
        type: 'warning',
        message: 'Seleccione un contrato para la fecha de salida.',
        dismissBtn: true
      });
      return;
    }

    if (!date) {
      this.customMessageService.open({
        title: 'Validación',
        type: 'warning',
        message: 'Seleccione una fecha de salida.',
        dismissBtn: true
      });
      return;
    }

    if (!fileNumber) {
      this.customMessageService.open({
        title: 'Validación',
        type: 'warning',
        message: 'Ingrese un número de folio para la fecha de salida.',
        dismissBtn: true
      });
      return;
    }

    // Convertir fecha a string ISO
    const dateString = new Date(date).toISOString().split('T')[0];

    // Verificar duplicados
    const existingExit = this.exitDate.find(
      exit => exit.contract === contract.id && exit.date === dateString
    );

    if (existingExit) {
      this.customMessageService.open({
        title: 'Validación',
        type: 'warning',
        message: 'Ya existe una fecha de salida para este contrato en esta fecha.',
        dismissBtn: true
      });
      return;
    }

    // Agregar nueva salida
    this.exitDate.push({
      contract: contract.id,
      date: dateString,
      fileNumber: fileNumber,
      pdf: pdf
    });

    // Limpiar formulario
    this.formGroup.patchValue({
      newExitContract: null,
      newExitDate: null,
      newExitFileNumber: '',
      newExitPDF: false
    });

    this.cdr.detectChanges();
    log.debug('Nueva fecha de salida agregada:', this.exitDate);
  }

  /**@description Eliminar fecha de entrada */
  removeEntryDate(contract: string, date: string) {
    const initialLength = this.entryDate.length;
    this.entryDate = this.entryDate.filter(
      entry => !(entry.contract === contract && entry.date === date)
    );

    if (this.entryDate.length < initialLength) {
      this.customMessageService.open({
        title: 'Éxito',
        type: 'success',
        message: 'Fecha de entrada eliminada correctamente.',
        duration: 2000
      });
      this.cdr.detectChanges();
      log.debug('Fecha de entrada eliminada:', { contract, date });
    }
  }

  /**@description Eliminar fecha de salida */
  removeExitDate(contract: string, date: string) {
    const initialLength = this.exitDate.length;
    this.exitDate = this.exitDate.filter(
      exit => !(exit.contract === contract && exit.date === date)
    );

    if (this.exitDate.length < initialLength) {
      this.customMessageService.open({
        title: 'Éxito',
        type: 'success',
        message: 'Fecha de salida eliminada correctamente.',
        duration: 2000
      });
      this.cdr.detectChanges();
      log.debug('Fecha de salida eliminada:', { contract, date });
    }
  }

  /**@description TrackBy function para fechas de entrada */
  trackByEntryDate(index: number, item: any): string {
    return `entry-${item.contract}-${item.date}`;
  }

  /**@description TrackBy function para fechas de salida */
  trackByExitDate(index: number, item: any): string {
    return `exit-${item.contract}-${item.date}`;
  }

  /**@description Función para actualizar equipamiento */
  async update() {
    try {
      let data = JSON.parse(JSON.stringify(this.record));
      
      // Actualizar con las fechas de entrada y salida
      data['entryDate'] = this.entryDate;
      data['exitDate'] = this.exitDate;
      data['id'] = this.record['id'];
      data['operationType'] = 'single'
      log.debug('Datos a guardar:', data);

      this.customMessageService.open({
        title: 'Espere',
        type: 'success',
        message: 'Guardando fechas de entrada y salida',
        loading: true
      });
      
      let apiResponse = await this.inventoryEquipmentsService.update(data);
      this.customMessageService.close();
      
      if (apiResponse.state == 'success') {
        await this.customMessageService.open({
          title: '¡Hecho!',
          type: 'success',
          message: apiResponse['message'],
          duration: 2000
        });
        
        // Incluir las fechas en la respuesta
        const responseData = {
          ...apiResponse.data,
          entryDate: this.entryDate,
          exitDate: this.exitDate
        };
        
        this.dialogRef.close(responseData);
      } else {
        await this.customMessageService.open({
          title: 'Error',
          type: 'error',
          message: apiResponse['message'],
          dismissBtn: true
        });
      }

    } catch (err) {
      this.customMessageService.close();
      await this.customMessageService.open({
        title: 'Error',
        type: 'error',
        message: err,
        dismissBtn: true
      });
    }
  }
/**@description Se abre ventana para agregar documento */
openUpload(data: any, type: string, dateType: 'entry' | 'exit') {
  const dialogRef = this.dialog.open(DocumentsUploadDownloadFormComponent, {
    disableClose: true,
    width: '100%',
    data: {
      record: this.record,
      data: data,
      type: type
    },
  });

  dialogRef.afterClosed().subscribe(async result => {
    if (result && result.pdf === true) {
      log.debug('result', result);
      
      // Seleccionar el array correcto según el tipo
      const targetArray = dateType === 'entry' ? this.entryDate : this.exitDate;
      
      // Buscar y actualizar
      const index = targetArray.findIndex(
        item => item.contract === data.contract && item.date === data.date
      );
      
      if (index !== -1) {
        targetArray[index].pdf = true;
        this.cdr.detectChanges();
        
        const dateLabel = dateType === 'entry' ? 'entrada' : 'salida';
        log.debug(`PDF actualizado en ${dateLabel}:`, targetArray[index]);
        
        this.customMessageService.open({
          title: '¡Hecho!',
          type: 'success',
          message: `Documento de ${dateLabel} cargado exitosamente`,
          duration: 2000
        });
      } else {
        log.error('No se encontró el item para actualizar');
      }
    }
  });
}

  /**@description Se abre ventana para descargar consumo */
  openDownload(data,type){
    const dialogRef = this.dialog.open(DocumentsUploadDownloadFormComponent, {
      disableClose: true,
      width: '100%',
      data: {
        record: this.record,
        data: data,
        type: type
      },
    });

    dialogRef.afterClosed().subscribe(async result => {
    });
  }
}